<template>
  <div v-rect-loading="loading" class="edit-message-container">
    <div class="message-editor-header">
      <div class="message-editor-title">{{ $t(`${i18nPrefix}.label.${isView ? 'viewSystemNotification' : 'sendSystemNotification'}`) }}</div>
    </div>
    <div class="message-info-container">
      <WrappedFrom
        ref="form"
        class="message-info"
        label-width="220px"
        :model="messageInfo"
        :formLines="formLines"
      >
        <template #companyIds="{ lineName }">
          <el-select
            v-model="messageInfo[lineName]"
            clearable
            filterable
            multiple
            :disabled="isView"
          >
            <el-option
              v-for="(item, index) in companyOptions"
              :key="index"
              :style="{ paddingLeft: item.level * 15 + 'px' }"
              :label="item.i18nName"
              :value="item.id"
            ></el-option>
          </el-select>
        </template>
        <template #tinymceEditor="{ lineName }">
          <TinymceEditor v-model="messageInfo[lineName]" class="content-container" :disabled="isView"></TinymceEditor>
        </template>
      </WrappedFrom>
      <div class="bottom-btn-group">
        <el-button class="cancel-btn" @click="handleCancel">{{ $t('common.btn.cancel') }}</el-button>
        <el-button v-if="!isView" class="save-btn" type="primary" @click="handleSave">{{ $t('common.btn.save') }}</el-button>
      </div>
    </div>
  </div>
</template>

<script>
import _ from 'lodash'
import { addI18nProperty } from '@/i18n'
import { mapActions } from 'vuex'
import WrappedFrom from '@/components/formEditor/wrappedForm.vue'
import TinymceEditor from './tinymceEditor.vue'
import { COMPANY_STATUS } from '@/constant/company'
import { mixedRouterPush } from '@/router/routes'

export default {
  components: {
    WrappedFrom,
    TinymceEditor
  },
  data () {
    return {
      i18nPrefix: 'admin.company.siteMessageManagement',
      loading: false,
      companyOptions: [],
      messageInfo: {
        company_ids: [],
        is_sub_company: true,
        is_exclude: false, // 可选范围 仅所选公司:false | 除所选公司外:true
        title: null,
        content: null
      }
    }
  },
  computed: {
    siteMessageId () {
      return this.$route.params.siteMessageId
    },
    isView () {
      return this.$route.meta.isView
    },
    formLines () {
      const hasCompany = this.messageInfo.company_ids && this.messageInfo.company_ids.length > 0

      const lines = {
        company_ids: {
          contentSlot: 'companyIds',
          placeholder: this.$t(`${this.i18nPrefix}.label.allCompanies`),
          label: this.$t(`${this.i18nPrefix}.label.visibleCompanies`)
        },
        is_sub_company: {
          elType: 'el-radio-group',
          label: this.$t(`${this.i18nPrefix}.label.includeSubCompaniesLabel`),
          disabled: this.isView || !hasCompany,
          option: {
            data: [
              { label: this.$t(`${this.i18nPrefix}.label.yes`), value: true },
              { label: this.$t(`${this.i18nPrefix}.label.no`), value: false }
            ]
          }
        },
        is_exclude: {
          elType: 'el-radio-group',
          label: this.$t(`${this.i18nPrefix}.label.includeExclude`),
          disabled: this.isView || !hasCompany,
          option: {
            data: [
              { label: this.$t(`${this.i18nPrefix}.label.includeCompanies`), value: false },
              { label: this.$t(`${this.i18nPrefix}.label.excludeCompanies`), value: true }
            ]
          }
        },
        title: {
          label: this.$t(`${this.i18nPrefix}.label.messageTitle`),
          disabled: this.isView,
          rules: [{ required: true, message: this.$t('common.message.requiredFormField'), trigger: 'blur' }]
        },
        content: {
          contentSlot: 'tinymceEditor',
          label: this.$t(`${this.i18nPrefix}.label.messageTemplate`),
          rules: [
            { required: true, message: this.$t('common.message.requiredFormField') },
            {
              trigger: 'blur',
              validator: (rule, value, callback) => {
                if (!value) {
                  callback(new Error(this.$t('common.message.requiredFormField')))
                } else {
                  callback()
                }
              }
            }
          ]
        }
      }

      return lines
    }
  },
  created () {
    this.initPage()
  },
  methods: {
    ...mapActions('superAdmin', ['getSuperAdminCompanyList']),
    ...mapActions('siteMessage', ['getSiteMessageInfoByMessageId', 'createSiteMessage']),
    async initPage () {
      this.loading = true
      try {
        await Promise.all([
          this.loadSiteMessageCompanyList(),
          this.loadMessageInfo()
        ])
      } finally {
        this.loading = false
      }
    },
    async loadSiteMessageCompanyList () {
      const resp = await this.getSuperAdminCompanyList({ status: COMPANY_STATUS.APPROVED })
      if (resp && resp.result) {
        this.companyOptions = _.chain(resp.result)
          .filter(item => !item.parent_id && !item.root_company_id)
          .flatMap(root => {
            root.name = `${root.id} - ${root.name}`
            root.cn_name = `${root.id} - ${root.cn_name}`
            addI18nProperty(root, 'i18nName', 'cn_name', 'name')
            root.level = 1
            const children = this.formatCompanies(resp.result, root.id, 2)
            return [root, ...children]
          }).value()
      }
    },
    formatCompanies (companies, parentId, level) {
      const children = _.filter(companies, item => item.parent_id === parentId)
      return _.flatMap(children, child => {
        child.name = `${child.id} - ${child.name}`
        child.cn_name = `${child.id} - ${child.cn_name}`
        addI18nProperty(child, 'i18nName', 'cn_name', 'name')
        const withLevel = { ...child, level }
        const descendants = this.formatCompanies(companies, child.id, level + 1)
        return [withLevel, ...descendants]
      })
    },
    async loadMessageInfo () {
      if (!this.siteMessageId) {
        return
      }
      const resp = await this.getSiteMessageInfoByMessageId(this.siteMessageId)
      if (resp) {
        this.messageInfo = resp
      }
    },
    handleCancel () {
      const backUrl = this.$route.query.back
      if (backUrl) {
        mixedRouterPush(this.$route.query.back)
      } else {
        this.$router.go(-1)
      }
    },
    async handleSave () {
      const result = await this.$refs.form.submit()
      if (!result) {
        return
      }

      this.loading = true
      try {
        await this.createSiteMessage(this.messageInfo)
        this.$message({
          type: 'success',
          message: this.$t(`${this.i18nPrefix}.message.saveSiteMessageSuccess`)
        })
        this.handleCancel()
      } catch (error) {
        this.$message({
          type: 'error',
          message: this.$t(`${this.i18nPrefix}.message.saveSiteMessageFail`)
        })
      } finally {
        this.loading = false
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.edit-message-container {
  .message-editor-header {
    display: flex;
    border-bottom: 1px solid #e4e7ed;
    .message-editor-title {
      padding-top: 4px;
      padding-bottom: 18px;
      line-height: 25px;
      font-size: 18px;
      font-weight: 500;
      color: #303133;
    }
  }
  .message-info-container {
    width: 900px;
    margin: 30px auto 0;
    .bottom-btn-group {
      display: flex;
      justify-content: flex-end;
      margin: 30px auto 0;
      .el-button {
        padding: 8px 28px;
        border-radius: 2px;
        line-height: 22px;
        font-size: 16px;
        font-weight: 500;
        color: #0096cc;
        &:last-child {
          margin-left: 24px;
          margin-right: 0;
          background: #0096cc;
          border-color: #0096cc;
          color: #ffffff;
        }
      }
    }
  }
}
</style>
